// src/pages/Home/FormContact.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { saveMessageContactForm } from "../../services/contactformServices";
import {
  FaPaperPlane,
  FaEnvelope,
  FaUser,
  FaCommentDots,
  FaCheckCircle,
  FaExclamationTriangle,
  FaPhoneAlt,
  FaCalendarAlt,
  FaVideo,
  FaBuilding,
  FaLayerGroup,
} from "react-icons/fa";

const cx = (...c) => c.filter(Boolean).join(" ");

// Remplace par tes vrais liens
const VISIO_BOOKING_URL = "https://calendar.app.google/Sot2oRDmmUbkpJZy5";

const productOptions = [
  "MonAppliClub",
  'MonAppliClub "Light"',
  "MonAppliMairie",
  "MonAppliAsso",
  "MonAppliTournoi",
  "Judo Coach Pro",
  "Judo Présence Pro",
  "MonAppliKata",
  "MonAppliCaté",
  "Randori Cards",
  "Autre / Je ne sais pas encore",
];

const Field = ({ label, icon: Icon, error, children, required = false }) => (
  <div>
    <label className="text-sm font-semibold text-slate-900 dark:text-white">
      {label} {required && <span className="text-orange-500">*</span>}
    </label>

    <div className="relative mt-2">
      {Icon && (
        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
          <Icon />
        </div>
      )}

      {children}
    </div>

    {error && (
      <div className="mt-2 flex items-center gap-2 text-sm text-rose-600 dark:text-rose-400">
        <FaExclamationTriangle className="text-xs" />
        <span>{error}</span>
      </div>
    )}
  </div>
);

function ActionCard({ icon, title, text, href, buttonLabel, variant = "sky" }) {
  const iconWrap =
    variant === "sky"
      ? "bg-sky-100 text-sky-600 dark:bg-sky-500/10 dark:text-sky-400"
      : "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400";

  const buttonStyle =
    variant === "sky"
      ? "bg-sky-500 text-white hover:bg-sky-600"
      : "bg-emerald-500 text-white hover:bg-emerald-600";

  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="flex items-start gap-4">
        <div className={cx("grid h-12 w-12 place-items-center rounded-2xl", iconWrap)}>
          {icon}
        </div>

        <div className="min-w-0">
          <div className="text-lg font-semibold text-slate-900 dark:text-white">
            {title}
          </div>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {text}
          </p>
        </div>
      </div>

      {href && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={cx(
            "mt-4 inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition",
            buttonStyle
          )}
        >
          {buttonLabel}
        </a>
      )}
    </div>
  );
}

const FormContact = () => {
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [touched, setTouched] = useState({});
  const firstInvalidRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    product: "",
    message: "",
  });

  const validators = useMemo(
    () => ({
      email: (v) => {
        const val = String(v || "").trim();
        if (!val) return "Email obligatoire.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return "Email invalide.";
        return "";
      },
      name: (v) => {
        const val = String(v || "").trim();
        if (!val) return "Nom / Prénom obligatoire.";
        if (val.length < 2) return "Nom / Prénom trop court.";
        return "";
      },
      message: (v) => {
        const val = String(v || "").trim();
        if (!val) return "Message obligatoire.";
        if (val.length < 10) return "Message trop court.";
        return "";
      },
    }),
    []
  );

  const errors = useMemo(
    () => ({
      email: validators.email(formData.email),
      name: validators.name(formData.name),
      message: validators.message(formData.message),
    }),
    [formData, validators]
  );

  const isValid = useMemo(
    () => !errors.email && !errors.name && !errors.message,
    [errors]
  );

  useEffect(() => {
    if (status !== "error") return;
    firstInvalidRef.current?.focus?.();
  }, [status]);

  useEffect(() => {
    firstInvalidRef.current = null;
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    setStatus("idle");
    setErrorMsg("");
  };

  const handleBlur = (e) => {
    setTouched((p) => ({ ...p, [e.target.name]: true }));
  };

  const getInvalidRef = (fieldName) => {
    if (!firstInvalidRef.current && errors[fieldName]) {
      return (el) => {
        if (el) firstInvalidRef.current = el;
      };
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setTouched({ name: true, email: true, message: true });

    if (!isValid) {
      setStatus("error");
      setErrorMsg("Merci de corriger les champs.");
      return;
    }

    setStatus("sending");
    setErrorMsg("");

    try {
      const composedMessage = `
Téléphone : ${formData.phone || "Non renseigné"}
Structure / Club : ${formData.organization || "Non renseigné"}
Produit : ${formData.product || "Non renseigné"}

Message :
${formData.message}
      `.trim();

      await saveMessageContactForm({
        name: formData.name,
        email: formData.email,
        message: composedMessage,
      });

      setStatus("sent");
      setFormData({
        name: "",
        email: "",
        phone: "",
        organization: "",
        product: "",
        message: "",
      });
      setTouched({});
    } catch (error) {
      console.error("Erreur envoi formulaire contact:", error);
      setStatus("error");
      setErrorMsg("Une erreur est survenue.");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-start">
      {/* Colonne gauche */}
      <aside className="space-y-4 lg:col-span-5">
        <ActionCard
          icon={<FaPaperPlane />}
          title="Envoyer un message depuis ce formulaire"
          text="Décrivez simplement votre besoin."
        />

        {/* <ActionCard
          icon={<FaPhoneAlt />}
          title="Rendez-vous téléphonique"
          text="Choisissez un créneau d’appel."
          href={PHONE_BOOKING_URL}
          buttonLabel="Réserver un appel"
          variant="sky"
        /> */}

        <ActionCard
          icon={<FaVideo />}
          title="Rendez-vous visio"
          text="Planifiez une démonstration en ligne."
          href={VISIO_BOOKING_URL}
          buttonLabel="Réserver une visio"
          variant="emerald"
        />
      </aside>

      {/* Colonne droite */}
      <div className="lg:col-span-7">
        <div className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900 sm:p-8">
          {status === "sent" && (
            <div className="mb-5 flex items-center gap-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-emerald-700 dark:text-emerald-300">
              <FaCheckCircle />
              <div className="text-sm font-semibold">
                Message envoyé.
              </div>
            </div>
          )}

          {status === "error" && errorMsg && (
            <div className="mb-5 flex items-center gap-3 rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-rose-700 dark:text-rose-300">
              <FaExclamationTriangle />
              <div className="text-sm font-semibold">{errorMsg}</div>
            </div>
          )}

          <div className="mb-6">
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
              Formulaire
            </div>
            <h3 className="mt-3 text-2xl font-bold text-slate-900 dark:text-white">
              Laissez votre message
            </h3>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field
                label="Email"
                icon={FaEnvelope}
                required
                error={touched.email ? errors.email : ""}
              >
                <input
                  ref={getInvalidRef("email")}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder="mail@domaine.fr"
                  autoComplete="email"
                  className={cx(
                    "h-12 w-full rounded-2xl border pl-11 pr-4 text-sm",
                    "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70",
                    "dark:border-white/10 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500",
                    touched.email && errors.email && "border-rose-500/60"
                  )}
                />
              </Field>

              <Field
                label="Nom / Prénom"
                icon={FaUser}
                required
                error={touched.name ? errors.name : ""}
              >
                <input
                  ref={getInvalidRef("name")}
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder="Votre nom"
                  autoComplete="name"
                  className={cx(
                    "h-12 w-full rounded-2xl border pl-11 pr-4 text-sm",
                    "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70",
                    "dark:border-white/10 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500",
                    touched.name && errors.name && "border-rose-500/60"
                  )}
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field label="Téléphone" icon={FaPhoneAlt}>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder="06 00 00 00 00"
                  autoComplete="tel"
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
                />
              </Field>

              <Field label="Club / Structure" icon={FaBuilding}>
                <input
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder="Nom de votre structure"
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
                />
              </Field>
            </div>

            <Field label="Produit" icon={FaLayerGroup}>
              <select
                name="product"
                value={formData.product}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70 dark:border-white/10 dark:bg-slate-950 dark:text-white"
              >
                <option value="">Choisir un produit</option>
                {productOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Message"
              icon={FaCommentDots}
              required
              error={touched.message ? errors.message : ""}
            >
              <textarea
                ref={getInvalidRef("message")}
                name="message"
                rows={6}
                value={formData.message}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="Décrivez votre besoin..."
                className={cx(
                  "w-full resize-none rounded-2xl border px-4 py-3 pl-11 pr-4 text-sm",
                  "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70",
                  "dark:border-white/10 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500",
                  touched.message && errors.message && "border-rose-500/60"
                )}
              />
            </Field>

            <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
              <div className="text-xs text-slate-500 dark:text-slate-400">
                * Champs obligatoires
              </div>

              <button
                type="submit"
                disabled={status === "sending"}
                className={cx(
                  "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition",
                  "bg-sky-500 text-white hover:bg-sky-600",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70",
                  "disabled:cursor-not-allowed disabled:opacity-60"
                )}
              >
                {status === "sending" ? "Envoi..." : "Envoyer"}
                <FaPaperPlane className="text-sm" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FormContact;